import { isServerUploadUrl as isManagedServerUploadUrl } from '../../utils/assetUrl';

export const DEFAULT_CANVAS_WIDTH = 720;
export const DEFAULT_CANVAS_HEIGHT = 1000;

export { isManagedServerUploadUrl as isServerUploadUrl };

// Match legacy Pixie export list — do NOT include textObj (runtime-only button↔text links)
export const FABRIC_CUSTOM_PROPS = [
  'selectable', 'name', 'senderNameChecked', 'useGoogleMap', 'selectedMapType',
  'useUserGPS', 'corners', 'vertical', 'horizontal', 'niceName', 'action',
  'iconfill', 'imagetype', 'zorder', 'url', 'uploadUrl', 'buttonSavedFill',
];

/** Break button/text/icon runtime links before Fabric JSON export */
function detachRuntimeLinks(fabricCanvas) {
  const restored = [];
  fabricCanvas.forEachObject((obj) => {
    if (obj.textObj) {
      restored.push({ obj, textObj: obj.textObj });
      delete obj.textObj;
    }
  });
  return restored;
}

function restoreRuntimeLinks(restored) {
  restored.forEach(({ obj, textObj }) => {
    obj.textObj = textObj;
  });
}

const TEXT_EXPORT_TYPES = new Set(['text', 'textbox', 'i-text']);

/** Fabric stylesToArray crashes if text.styles is undefined */
export function sanitizeObjectForFabricExport(obj) {
  if (!obj) return;

  if (TEXT_EXPORT_TYPES.has(obj.type)) {
    if (obj.styles == null || typeof obj.styles !== 'object') {
      obj.styles = {};
    }
    const lines = String(obj.text ?? '').split('\n');
    for (let i = 0; i < lines.length; i += 1) {
      if (obj.styles[i] == null || typeof obj.styles[i] !== 'object') {
        obj.styles[i] = {};
      }
    }
  }

  const children = typeof obj.getObjects === 'function' ? obj.getObjects() : obj._objects;
  if (children?.length) {
    children.forEach(sanitizeObjectForFabricExport);
  }
}

export function sanitizeCanvasForFabricExport(fabricCanvas) {
  fabricCanvas?.forEachObject?.(sanitizeObjectForFabricExport);
}

function exportCanvasJson(fabricCanvas) {
  sanitizeCanvasForFabricExport(fabricCanvas);
  try {
    return fabricCanvas.toDatalessJSON
      ? fabricCanvas.toDatalessJSON(FABRIC_CUSTOM_PROPS)
      : fabricCanvas.toJSON(FABRIC_CUSTOM_PROPS);
  } catch (err) {
    console.warn('toDatalessJSON failed, falling back to toJSON', err);
    return fabricCanvas.toJSON(FABRIC_CUSTOM_PROPS);
  }
}

export function parseDimensions(raw) {
  try {
    const dims = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const width = Number(dims?.width) || 0;
    const height = Number(dims?.height) || 0;
    if (width < 50 || height < 50) {
      return { width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT };
    }
    return { width, height };
  } catch {
    return { width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT };
  }
}

export function color2rgba(color) {
  if (!color) return '0,0,0,1';
  const normalized = String(color).trim().toLowerCase();
  if (normalized === 'transparent') return '0,0,0,0';
  if (color.indexOf('#') === 0) {
    const h = color.replace('#', '');
    const result = [];
    if (h.length === 3) {
      for (let i = 0; i < h.length; i += 1) {
        result[i] = parseInt(h[i] + h[i], 16);
      }
    } else {
      for (let i = 0; i < 3; i += 1) {
        result[i] = parseInt(h[i * 2] + h[i * 2 + 1], 16);
      }
    }
    return `${result.join()},1`;
  }
  if (color.indexOf('rgba') === 0) {
    return color.replace('rgba(', '').replace(')', '').split(' ').join('');
  }
  if (color.indexOf('rgb') === 0) {
    const parts = color.replace('rgb(', '').replace(')', '').split(',').map((v) => v.trim());
    return `${parts.join()},1`;
  }
  return '0,0,0,1';
}

export function getEditableData(fabricCanvas, original) {
  const list = [];
  fabricCanvas.forEachObject((obj) => {
    if (obj?.name === 'editable' || obj?.name === 'entryfield') {
      list.push({
        zorder: obj.zorder || 0,
        top: obj.top / original.height,
        left: obj.left / original.width,
        height: obj.height / original.height,
        width: obj.width / original.width,
        type: obj.name,
      });
    }
  });
  return list;
}

export function getButtonData(fabricCanvas, original) {
  const list = [];
  fabricCanvas.forEachObject((button) => {
    if (
      button?.niceName &&
      (button.niceName.indexOf('button rect') === 0 ||
        button.niceName.indexOf('button circle') === 0 ||
        button.niceName.indexOf('button ellipse') === 0)
    ) {
      const data = (button.action || '').split(/:(.+)?/);
      const actionType = data[0] || '';
      const action = data[1] || '';
      const text = button.textObj;
      let icon = null;
      if (text?.textObj) icon = text.textObj.url;

      list.push({
        top: button.top / original.height,
        left: button.left / original.width,
        height: (button.height * (button.scaleY || 1)) / original.height,
        width: (button.width * (button.scaleX || 1)) / original.width,
        corner: button.rx || 0,
        icon,
        zorder: button.zorder || 0,
        style: button.name,
        text: text?.text || '',
        textColor: color2rgba(text?.fill),
        fontfamily: text?.fontFamily || 'Open Sans',
        fontsize: text?.fontSize || 24,
        color: color2rgba(button.fill),
        actionType,
        action,
      });
    }
  });
  return list;
}

export function getTextData(fabricCanvas, original) {
  const list = [];
  fabricCanvas.forEachObject((obj) => {
    if (
      obj.name === 'text' &&
      obj.niceName &&
      obj.niceName.indexOf('button') !== 0 &&
      obj.visible !== false
    ) {
      const angle = obj.angle || 0;
      obj.set({ angle: 0 }).setCoords();
      list.push({
        editable: obj.niceName === 'editable text',
        generated: obj.niceName === 'generated text',
        zorder: obj.zorder || 0,
        top: obj.top / original.height,
        left: obj.left / original.width,
        height: obj.height / original.height,
        width: obj.width / original.width,
        text: obj.text,
        fontfamily: obj.fontFamily,
        fontsize: obj.fontSize,
        color: color2rgba(obj.fill),
        angle,
        senderNameChecked: obj.senderNameChecked || false,
      });
      obj.set({ angle }).setCoords();
    }
  });
  return list;
}

export function getImageData(imageData, original) {
  const list = [];
  (imageData || []).forEach((image) => {
    const obj = image.obj;
    const includeImage =
      isOrgBannerObject(obj)
      || (
        obj?.niceName
        && (obj.niceName === 'image'
          || obj.niceName === 'sticker'
          || obj.name === 'sticker'
          || obj.niceName === 'shape')
        && !isEditableRegionObject(obj)
      );
    if (includeImage) {
      const angle = obj.angle || 0;
      obj.set({ angle: 0 }).setCoords();
      const bounds = typeof obj.getBoundingRect === 'function'
        ? obj.getBoundingRect(true, true)
        : null;
      const objHeight = bounds?.height ?? (obj.height * (obj.scaleY || 1));
      const objWidth = bounds?.width ?? (obj.width * (obj.scaleX || 1));
      list.push({
        url: image.url,
        zorder: obj.zorder || 0,
        top: (bounds?.top ?? obj.top) / original.height,
        left: (bounds?.left ?? obj.left) / original.width,
        height: objHeight / original.height,
        width: objWidth / original.width,
        angle,
        useGoogleMap: obj.useGoogleMap || false,
        selectedMapType: obj.selectedMapType || null,
        useUserGPS: obj.useUserGPS || false,
      });
      obj.set({ angle }).setCoords();
    }
  });
  return list;
}

export function buildAppLayout(fabricCanvas, original, backgroundColor, previewImagePath, imageData = []) {
  const layoutJson = typeof backgroundColor === 'string' && backgroundColor.startsWith('{')
    ? JSON.parse(backgroundColor)
    : { background: backgroundColor };
  const bg = layoutJson.background || backgroundColor || '#FFFFFF';
  const applayout = {
    backgroundColor: color2rgba(bg),
    previewImage: previewImagePath,
    buttonLayout: getButtonData(fabricCanvas, original),
    textLayout: getTextData(fabricCanvas, original),
    imageLayout: getImageData(imageData, original),
    editableLayout: getEditableData(fabricCanvas, original),
  };
  return applyIosApplayoutZOrder(applayout);
}

export function getDesignPayload(fabricCanvas, original, editorCustomActions = {}) {
  fabricCanvas.discardActiveObject();
  fabricCanvas.renderAll();

  const restored = detachRuntimeLinks(fabricCanvas);
  let fabricData;
  try {
    fabricData = exportCanvasJson(fabricCanvas);
  } finally {
    restoreRuntimeLinks(restored);
  }

  return {
    layout: JSON.stringify(fabricData),
    actions: JSON.stringify(editorCustomActions),
    dimensions: JSON.stringify({ width: original.width, height: original.height }),
  };
}

export function relinkButtonObjects(fabricCanvas) {
  let buttonCount = 0;
  fabricCanvas.forEachObject((obj) => {
    if (
      obj.niceName &&
      (obj.niceName.indexOf('button rect') === 0 ||
        obj.niceName.indexOf('button circle') === 0 ||
        obj.niceName.indexOf('button ellipse') === 0)
    ) {
      buttonCount += 1;
      const number = parseInt(
        obj.niceName.replace('button rect ', '').replace('button circle ', '').replace('button ellipse ', ''),
        10
      );
      fabricCanvas.forEachObject((text) => {
        if (text.niceName === `button text ${number}`) {
          obj.textObj = text;
          text.textObj = obj;
        }
      });
      if (obj.textObj) {
        fabricCanvas.forEachObject((icon) => {
          if (icon.niceName === `button icon ${number}`) {
            obj.textObj.textObj = icon;
            icon.textObj = obj;
          }
        });
      }
    }
  });
  return buttonCount;
}

/** Alias for plan/docs — same as shouldIncludeInImageUpload */
export function objectsForImageUpload(fabricCanvas) {
  const list = [];
  fabricCanvas?.forEachObject?.((obj) => {
    if (shouldIncludeInImageUpload(obj)) list.push(obj);
  });
  return list;
}

function isOrgBannerObject(obj) {
  return obj?.name === 'orgbanner' || obj?.niceName === 'orgbanner';
}

function isEditableRegionObject(obj) {
  return obj?.name === 'editable'
    || obj?.name === 'entryfield'
    || obj?.niceName === 'editable'
    || obj?.niceName === 'entryfield';
}

/** Match legacy TopPanelController.saveEditedImages object filter */
export function shouldIncludeInImageUpload(obj) {
  if (!obj || obj.visible === false) return false;
  if (isEditableRegionObject(obj)) return false;
  if (isOrgBannerObject(obj)) return true;
  if (
    obj.niceName === 'shape'
    || obj.niceName === 'image'
    || obj.niceName === 'sticker'
    || obj.name === 'sticker'
    || obj.name === 'icon'
  ) {
    return true;
  }
  return false;
}

/** Build imageData array for buildAppLayout from canvas objects */
export function collectImageData(fabricCanvas) {
  const data = [];
  fabricCanvas.forEachObject((obj) => {
    if (!obj) return;
    const isImageType =
      obj.type === 'image'
      || obj.type === 'group'
      || obj.name === 'sticker'
      || obj.niceName === 'image'
      || obj.name === 'image';
    if (!isImageType || obj.name === 'editable' || obj.name === 'entryfield') return;

    let url = obj.uploadUrl || obj.src || '';
    if (!url && typeof obj.getSrc === 'function') {
      try {
        url = obj.getSrc() || '';
      } catch {
        url = '';
      }
    }
    if (!url && obj.type === 'group') {
      url = obj.src || '';
    }
    if (url) {
      data.push({ obj, url });
    }
  });
  return data;
}

/**
 * iOS native renderer: lower zorder draws on top (legacy you-app org alerts).
 * editableLayout → 0, imageLayout → 1..n (single image → 2), textLayout → 1
 * useGoogleMap overlays → 1 (above static images); statics → 2+ when maps present
 */
export function applyIosApplayoutZOrder(applayout) {
  if (!applayout || typeof applayout !== 'object') return applayout;

  const copy = JSON.parse(JSON.stringify(applayout));

  if (Array.isArray(copy.editableLayout)) {
    copy.editableLayout = copy.editableLayout.map((entry) => ({
      ...entry,
      zorder: 0,
    }));
  }

  if (Array.isArray(copy.textLayout) && copy.textLayout.length > 0) {
    copy.textLayout = copy.textLayout.map((entry) => ({
      ...entry,
      zorder: 1,
    }));
  }

  if (Array.isArray(copy.imageLayout) && copy.imageLayout.length > 0) {
    const count = copy.imageLayout.length;
    const hasMaps = copy.imageLayout.some((e) => e?.useGoogleMap);
    let staticCounter = 0;

    copy.imageLayout = copy.imageLayout.map((entry, index) => {
      if (entry?.useGoogleMap) {
        return { ...entry, zorder: 1 };
      }
      if (!hasMaps) {
        const zorder = count === 1 ? 2 : index + 1;
        return { ...entry, zorder };
      }
      staticCounter += 1;
      return { ...entry, zorder: staticCounter + 1 };
    });
  }

  return copy;
}

export function compareAppLayoutSnapshots(a, b) {
  const keys = ['backgroundColor', 'buttonLayout', 'textLayout', 'imageLayout', 'editableLayout'];
  const diffs = [];
  keys.forEach((key) => {
    const left = JSON.stringify(a?.[key] ?? null);
    const right = JSON.stringify(b?.[key] ?? null);
    if (left !== right) diffs.push(key);
  });
  return { equal: diffs.length === 0, diffs };
}
